import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import * as XLSX from "xlsx";

import {
  jsPDF,
} from "jspdf";

import autoTable from "jspdf-autotable";

import {
  getConsumosPeajes,
} from "../helpers/rutaPeajes";

/* ==================================================
   FECHAS
================================================== */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const firstDayMonthISO =
  () => {
    const d = new Date();

    d.setDate(1);

    d.setMinutes(
      d.getMinutes() -
        d.getTimezoneOffset()
    );

    return d
      .toISOString()
      .slice(0, 10);
  };

/* ==================================================
   FORMATOS
================================================== */

const formatoFecha = (
  value
) => {
  if (!value) {
    return "-";
  }

  const d =
    new Date(value);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "-";
  }

  return `${String(
    d.getUTCDate()
  ).padStart(
    2,
    "0"
  )}/${String(
    d.getUTCMonth() + 1
  ).padStart(
    2,
    "0"
  )}/${d.getUTCFullYear()}`;
};

const formatoMoneda = (
  value
) => {
  return Number(
    value || 0
  ).toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
    }
  );
};

const formatoNumero = (
  value
) => {
  return Number(
    value || 0
  ).toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const proveedorTexto = (
  value
) => {
  if (
    value ===
    "TELEPEAJE_PLUS"
  ) {
    return "Telepeaje Plus";
  }

  if (
    value ===
    "TELEPASE"
  ) {
    return "TelePASE";
  }

  return value || "-";
};

const estadoVariant = (
  value
) => {
  if (
    value ===
    "VINCULADO"
  ) {
    return "success";
  }

  if (
    value ===
    "PATENTE_NO_ENCONTRADA"
  ) {
    return "danger";
  }

  if (
    value ===
    "SIN_PATENTE"
  ) {
    return "warning";
  }

  return "secondary";
};

const estadoTexto = (
  value
) => {
  if (
    value ===
    "VINCULADO"
  ) {
    return "Vinculado";
  }

  if (
    value ===
    "PATENTE_NO_ENCONTRADA"
  ) {
    return "No encontrado";
  }

  if (
    value ===
    "SIN_PATENTE"
  ) {
    return "Sin patente";
  }

  return value || "-";
};

/* ==================================================
   COMPONENTE
================================================== */

const InformePeajes =
  () => {
    const [
      desde,
      setDesde,
    ] = useState(
      firstDayMonthISO()
    );

    const [
      hasta,
      setHasta,
    ] = useState(
      todayLocalISO()
    );

    const [
      proveedor,
      setProveedor,
    ] = useState("");

    const [
      patente,
      setPatente,
    ] = useState("");

    const [
      concesionario,
      setConcesionario,
    ] = useState("");

    const [
      estadoVinculacion,
      setEstadoVinculacion,
    ] = useState("");

    /*
      Registros de la página
      visible.
    */
    const [
      consumos,
      setConsumos,
    ] = useState([]);

    /*
      TODOS los registros según
      los filtros aplicados.

      Se utilizan para:
      - resumen por vehículo
      - Excel
      - PDF
    */
    const [
      todosConsumos,
      setTodosConsumos,
    ] = useState([]);

    const [
      cantidad,
      setCantidad,
    ] = useState(0);

    const [
      totalImporte,
      setTotalImporte,
    ] = useState(0);

    const [
      pagina,
      setPagina,
    ] = useState(1);

    const [
      paginas,
      setPaginas,
    ] = useState(1);

    const [
      filtrosAplicados,
      setFiltrosAplicados,
    ] = useState(null);

    const [
      loading,
      setLoading,
    ] = useState(false);

    const [
      exportando,
      setExportando,
    ] = useState(false);

    const [
      error,
      setError,
    ] = useState("");

    /* ================================================
       FILTROS ACTUALES
    ================================================ */

    const obtenerFiltros =
      () => ({
        desde,
        hasta,
        proveedor,

        patente:
          patente.trim(),

        concesionario:
          concesionario.trim(),

        estadoVinculacion,
      });

    /* ================================================
       TRAER TODOS LOS REGISTROS FILTRADOS

       El backend permite hasta 500 por página.
       Si algún día tenemos más de 500,
       seguimos trayendo páginas.
    ================================================ */

    const obtenerTodos =
      async (
        filtros
      ) => {
        const limite =
          500;

        const primera =
          await getConsumosPeajes(
            {
              ...filtros,

              pagina: 1,
              limite,
            }
          );

        if (
          !primera?.ok
        ) {
          throw new Error(
            primera?.error ||
              "No se pudieron obtener todos los consumos."
          );
        }

        let todos =
          Array.isArray(
            primera.consumos
          )
            ? primera.consumos
            : [];

        const total =
          Number(
            primera.cantidad ||
              todos.length ||
              0
          );

        const totalPaginas =
          Math.max(
            Math.ceil(
              total /
                limite
            ),
            1
          );

        for (
          let p = 2;
          p <= totalPaginas;
          p += 1
        ) {
          const respuesta =
            await getConsumosPeajes(
              {
                ...filtros,

                pagina: p,
                limite,
              }
            );

          if (
            !respuesta?.ok
          ) {
            throw new Error(
              respuesta?.error ||
                "No se pudieron obtener todos los consumos."
            );
          }

          const lote =
            Array.isArray(
              respuesta.consumos
            )
              ? respuesta.consumos
              : [];

          todos =
            todos.concat(
              lote
            );
        }

        return todos;
      };

    /* ================================================
       CARGAR UNA PÁGINA
    ================================================ */

    const cargarPagina =
      async (
        paginaSolicitada,
        filtros =
          filtrosAplicados
      ) => {
        if (!filtros) {
          return;
        }

        try {
          setLoading(
            true
          );

          setError("");

          const respuesta =
            await getConsumosPeajes(
              {
                ...filtros,

                pagina:
                  paginaSolicitada,

                limite: 100,
              }
            );

          if (
            !respuesta?.ok
          ) {
            throw new Error(
              respuesta?.error ||
                "No se pudieron obtener los consumos."
            );
          }

          setConsumos(
            Array.isArray(
              respuesta.consumos
            )
              ? respuesta.consumos
              : []
          );

          setCantidad(
            Number(
              respuesta.cantidad ||
                0
            )
          );

          setTotalImporte(
            Number(
              respuesta.totalImporte ||
                0
            )
          );

          setPagina(
            Number(
              respuesta.pagina ||
                1
            )
          );

          setPaginas(
            Math.max(
              Number(
                respuesta.paginas ||
                  1
              ),
              1
            )
          );
        } catch (err) {
          console.error(
            "Error cargando página de peajes:",
            err
          );

          setError(
            err?.message ||
              "No se pudo cargar el informe."
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    /* ================================================
       BUSCAR

       Trae:
       1) página visible
       2) todos los registros para resumen/exportación
    ================================================ */

    const buscar =
      async (
        filtrosPersonalizados =
          null
      ) => {
        const filtros =
          filtrosPersonalizados ||
          obtenerFiltros();

        if (
          !filtros.desde ||
          !filtros.hasta
        ) {
          setError(
            "Complete el rango de fechas."
          );

          return;
        }

        if (
          filtros.desde >
          filtros.hasta
        ) {
          setError(
            "La fecha Desde no puede ser posterior a Hasta."
          );

          return;
        }

        try {
          setLoading(
            true
          );

          setError("");

          const [
            respuestaPagina,
            todos,
          ] =
            await Promise.all([
              getConsumosPeajes(
                {
                  ...filtros,

                  pagina: 1,
                  limite: 100,
                }
              ),

              obtenerTodos(
                filtros
              ),
            ]);

          if (
            !respuestaPagina?.ok
          ) {
            throw new Error(
              respuestaPagina?.error ||
                "No se pudieron obtener los consumos."
            );
          }

          setFiltrosAplicados(
            filtros
          );

          setTodosConsumos(
            todos
          );

          setConsumos(
            Array.isArray(
              respuestaPagina.consumos
            )
              ? respuestaPagina.consumos
              : []
          );

          setCantidad(
            Number(
              respuestaPagina.cantidad ||
                0
            )
          );

          setTotalImporte(
            Number(
              respuestaPagina.totalImporte ||
                0
            )
          );

          setPagina(
            Number(
              respuestaPagina.pagina ||
                1
            )
          );

          setPaginas(
            Math.max(
              Number(
                respuestaPagina.paginas ||
                  1
              ),
              1
            )
          );
        } catch (err) {
          console.error(
            "Error informe peajes:",
            err
          );

          setError(
            err?.message ||
              "No se pudo cargar el informe."
          );

          setConsumos(
            []
          );

          setTodosConsumos(
            []
          );

          setCantidad(0);

          setTotalImporte(
            0
          );

          setPagina(1);

          setPaginas(1);
        } finally {
          setLoading(
            false
          );
        }
      };

    /* ================================================
       PRIMERA CARGA
    ================================================ */

    useEffect(() => {
      buscar();

      // eslint-disable-next-line
    }, []);

    /* ================================================
       LIMPIAR
    ================================================ */

    const limpiar =
      () => {
        const filtros =
          {
            desde:
              firstDayMonthISO(),

            hasta:
              todayLocalISO(),

            proveedor: "",
            patente: "",
            concesionario: "",
            estadoVinculacion:
              "",
          };

        setDesde(
          filtros.desde
        );

        setHasta(
          filtros.hasta
        );

        setProveedor("");
        setPatente("");
        setConcesionario("");

        setEstadoVinculacion(
          ""
        );

        buscar(
          filtros
        );
      };

    /* ================================================
       RESUMEN POR VEHÍCULO / PATENTE
    ================================================ */

    const resumenVehiculos =
      useMemo(() => {
        const mapa =
          new Map();

        todosConsumos.forEach(
          (item) => {
            const patenteItem =
              item.patente ||
              "SIN PATENTE";

            const vehiculo =
              item.vehiculo
                ?.camion ||
              "SIN VINCULAR";

            const id =
              item.vehiculo
                ?._id ||
              patenteItem;

            if (
              !mapa.has(id)
            ) {
              mapa.set(
                id,
                {
                  vehiculo,
                  patente:
                    patenteItem,

                  pasadas: 0,

                  importe: 0,
                }
              );
            }

            const registro =
              mapa.get(id);

            registro.pasadas +=
              1;

            registro.importe +=
              Number(
                item.importeFinal ||
                  0
              );
          }
        );

        return Array.from(
          mapa.values()
        )
          .map(
            (item) => ({
              ...item,

              importe:
                Number(
                  item.importe.toFixed(
                    2
                  )
                ),
            })
          )
          .sort(
            (a, b) =>
              b.importe -
              a.importe
          );
      }, [
        todosConsumos,
      ]);

    /* ================================================
       EXPORTAR EXCEL
    ================================================ */

    const exportarExcel =
      () => {
        if (
          todosConsumos.length ===
          0
        ) {
          return;
        }

        try {
          setExportando(
            true
          );

          const resumenExcel =
            resumenVehiculos.map(
              (item) => ({
                Vehiculo:
                  item.vehiculo,

                Patente:
                  item.patente,

                Pasadas:
                  item.pasadas,

                Importe:
                  item.importe,
              })
            );

          /*
            Agregamos total al final.
          */

          resumenExcel.push({
            Vehiculo:
              "TOTAL",

            Patente: "",

            Pasadas:
              todosConsumos.length,

            Importe:
              totalImporte,
          });

          const detalleExcel =
            todosConsumos.map(
              (item) => ({
                Fecha:
                  formatoFecha(
                    item.fecha
                  ),

                Hora:
                  item.hora ||
                  "",

                Sistema:
                  proveedorTexto(
                    item.proveedor
                  ),

                Patente:
                  item.patente ||
                  "",

                Vehiculo:
                  item.vehiculo
                    ?.camion ||
                  "",

                Concesionario:
                  item.concesionario ||
                  "",

                Estacion:
                  item.estacion ||
                  "",

                Via:
                  item.via ||
                  "",

                Tag:
                  item.dispositivo ||
                  "",

                Importe:
                  Number(
                    item.importeFinal ||
                      0
                  ),

                Estado:
                  estadoTexto(
                    item.estadoVinculacion
                  ),
              })
            );

          const wb =
            XLSX.utils.book_new();

          const wsResumen =
            XLSX.utils.json_to_sheet(
              resumenExcel
            );

          const wsDetalle =
            XLSX.utils.json_to_sheet(
              detalleExcel
            );

          /*
            Anchos aproximados.
          */

          wsResumen["!cols"] =
            [
              {
                wch: 28,
              },
              {
                wch: 14,
              },
              {
                wch: 12,
              },
              {
                wch: 18,
              },
            ];

          wsDetalle["!cols"] =
            [
              {
                wch: 12,
              },
              {
                wch: 10,
              },
              {
                wch: 18,
              },
              {
                wch: 12,
              },
              {
                wch: 24,
              },
              {
                wch: 26,
              },
              {
                wch: 45,
              },
              {
                wch: 14,
              },
              {
                wch: 14,
              },
              {
                wch: 16,
              },
              {
                wch: 18,
              },
            ];

          XLSX.utils.book_append_sheet(
            wb,
            wsResumen,
            "Resumen por Vehiculo"
          );

          XLSX.utils.book_append_sheet(
            wb,
            wsDetalle,
            "Detalle"
          );

          XLSX.writeFile(
            wb,
            `peajes-${filtrosAplicados?.desde || desde}-${filtrosAplicados?.hasta || hasta}.xlsx`
          );
        } catch (err) {
          console.error(
            "Error exportando Excel:",
            err
          );

          setError(
            "No se pudo generar el archivo Excel."
          );
        } finally {
          setExportando(
            false
          );
        }
      };

    /* ================================================
       EXPORTAR PDF
    ================================================ */

    const exportarPDF =
      () => {
        if (
          todosConsumos.length ===
          0
        ) {
          return;
        }

        try {
          setExportando(
            true
          );

          const doc =
            new jsPDF({
              orientation:
                "landscape",

              unit: "mm",
              format: "a4",
            });

          const filtros =
            filtrosAplicados ||
            obtenerFiltros();

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(
            15
          );

          doc.text(
            "Transporte DANLU - Informe de Peajes",
            10,
            12
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(
            9
          );

          doc.text(
            `Periodo: ${filtros.desde} a ${filtros.hasta}`,
            10,
            18
          );

          doc.text(
            `Sistema: ${
              filtros.proveedor
                ? proveedorTexto(
                    filtros.proveedor
                  )
                : "Todos"
            }`,
            10,
            23
          );

          doc.text(
            `Patente: ${
              filtros.patente ||
              "Todas"
            }`,
            70,
            23
          );

          doc.text(
            `Concesionario: ${
              filtros.concesionario ||
              "Todos"
            }`,
            125,
            23
          );

          doc.text(
            `Consumos: ${cantidad}`,
            10,
            29
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.text(
            `Total: ${formatoMoneda(
              totalImporte
            )}`,
            70,
            29
          );

          /* ----------------------------------------
             RESUMEN VEHÍCULOS
          ---------------------------------------- */

          autoTable(
            doc,
            {
              startY: 35,

              head: [
                [
                  "Vehículo",
                  "Patente",
                  "Pasadas",
                  "Importe",
                ],
              ],

              body:
                resumenVehiculos.map(
                  (item) => [
                    item.vehiculo,
                    item.patente,
                    String(
                      item.pasadas
                    ),
                    `$ ${formatoNumero(
                      item.importe
                    )}`,
                  ]
                ),

              foot: [
                [
                  "TOTAL",
                  "",
                  String(
                    todosConsumos.length
                  ),
                  `$ ${formatoNumero(
                    totalImporte
                  )}`,
                ],
              ],

              theme: "grid",

              styles: {
                fontSize: 8,
                cellPadding:
                  1.5,
              },

              headStyles: {
                fillColor: [
                  240,
                  240,
                  240,
                ],

                textColor: [
                  20,
                  20,
                  20,
                ],

                fontStyle:
                  "bold",
              },

              footStyles: {
                fillColor: [
                  240,
                  240,
                  240,
                ],

                textColor: [
                  20,
                  20,
                  20,
                ],

                fontStyle:
                  "bold",
              },

              columnStyles: {
                0: {
                  cellWidth:
                    65,
                },

                1: {
                  cellWidth:
                    35,
                },

                2: {
                  cellWidth:
                    30,

                  halign:
                    "right",
                },

                3: {
                  cellWidth:
                    45,

                  halign:
                    "right",
                },
              },
            }
          );

          let y =
            doc.lastAutoTable
              ?.finalY ||
            35;

          y += 8;

          if (y > 170) {
            doc.addPage();

            y = 15;
          }

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(
            11
          );

          doc.text(
            "Detalle de consumos",
            10,
            y
          );

          y += 4;

          /* ----------------------------------------
             DETALLE
          ---------------------------------------- */

          autoTable(
            doc,
            {
              startY: y,

              head: [
                [
                  "Fecha",
                  "Hora",
                  "Sistema",
                  "Patente",
                  "Vehículo",
                  "Concesionario",
                  "Estación",
                  "Vía",
                  "Tag",
                  "Importe",
                ],
              ],

              body:
                todosConsumos.map(
                  (item) => [
                    formatoFecha(
                      item.fecha
                    ),

                    item.hora ||
                      "",

                    proveedorTexto(
                      item.proveedor
                    ),

                    item.patente ||
                      "",

                    item.vehiculo
                      ?.camion ||
                      "",

                    item.concesionario ||
                      "",

                    item.estacion ||
                      "",

                    item.via ||
                      "",

                    item.dispositivo ||
                      "",

                    `$ ${formatoNumero(
                      item.importeFinal
                    )}`,
                  ]
                ),

              foot: [
                [
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "",
                  "TOTAL",

                  `$ ${formatoNumero(
                    totalImporte
                  )}`,
                ],
              ],

              theme: "grid",

              styles: {
                fontSize:
                  6.5,

                cellPadding:
                  1.2,

                overflow:
                  "linebreak",
              },

              headStyles: {
                fillColor: [
                  240,
                  240,
                  240,
                ],

                textColor: [
                  20,
                  20,
                  20,
                ],

                fontStyle:
                  "bold",
              },

              footStyles: {
                fillColor: [
                  240,
                  240,
                  240,
                ],

                textColor: [
                  20,
                  20,
                  20,
                ],

                fontStyle:
                  "bold",
              },

              columnStyles: {
                0: {
                  cellWidth:
                    17,
                },

                1: {
                  cellWidth:
                    15,
                },

                2: {
                  cellWidth:
                    24,
                },

                3: {
                  cellWidth:
                    18,
                },

                4: {
                  cellWidth:
                    31,
                },

                5: {
                  cellWidth:
                    34,
                },

                6: {
                  cellWidth:
                    55,
                },

                7: {
                  cellWidth:
                    18,
                },

                8: {
                  cellWidth:
                    20,
                },

                9: {
                  cellWidth:
                    25,

                  halign:
                    "right",
                },
              },

              margin: {
                left: 8,
                right: 8,
              },
            }
          );

          /*
            Numeración de páginas.
          */

          const totalPaginas =
            doc.getNumberOfPages();

          for (
            let p = 1;
            p <= totalPaginas;
            p += 1
          ) {
            doc.setPage(p);

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(
              7
            );

            doc.text(
              `Página ${p} de ${totalPaginas}`,
              287,
              202,
              {
                align:
                  "right",
              }
            );
          }

          doc.save(
            `peajes-${filtros.desde}-${filtros.hasta}.pdf`
          );
        } catch (err) {
          console.error(
            "Error exportando PDF:",
            err
          );

          setError(
            "No se pudo generar el PDF."
          );
        } finally {
          setExportando(
            false
          );
        }
      };

    /* ================================================
       RENDER
    ================================================ */

    return (
      <div className="container-fluid mt-4 mb-4">

        {/* CABECERA */}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">

          <div>
            <h4 className="mb-0">
              Informe de Peajes
            </h4>

            <small className="text-muted">
              Consulta de consumos importados de TelePASE y Telepeaje Plus
            </small>
          </div>

          <div className="mt-2">

            <Button
              variant="success"
              size="sm"
              className="mr-2"
              onClick={
                exportarExcel
              }
              disabled={
                loading ||
                exportando ||
                todosConsumos.length ===
                  0
              }
            >
              Exportar Excel
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className="mr-2"
              onClick={
                exportarPDF
              }
              disabled={
                loading ||
                exportando ||
                todosConsumos.length ===
                  0
              }
            >
              Exportar PDF
            </Button>

            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={
                limpiar
              }
              disabled={
                loading
              }
            >
              Limpiar
            </Button>

            <Button
              variant="dark"
              size="sm"
              onClick={() =>
                buscar()
              }
              disabled={
                loading
              }
            >
              {loading
                ? "Buscando..."
                : "Buscar"}
            </Button>
          </div>
        </div>

        {/* FILTROS */}

        <Card className="mb-3">
          <Card.Body>
            <Row>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Desde
                </Form.Label>

                <Form.Control
                  type="date"
                  size="sm"
                  value={
                    desde
                  }
                  onChange={(
                    e
                  ) =>
                    setDesde(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Hasta
                </Form.Label>

                <Form.Control
                  type="date"
                  size="sm"
                  value={
                    hasta
                  }
                  onChange={(
                    e
                  ) =>
                    setHasta(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Sistema
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    proveedor
                  }
                  onChange={(
                    e
                  ) =>
                    setProveedor(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todos
                  </option>

                  <option value="TELEPASE">
                    TelePASE
                  </option>

                  <option value="TELEPEAJE_PLUS">
                    Telepeaje Plus
                  </option>
                </Form.Control>
              </Col>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Patente
                </Form.Label>

                <Form.Control
                  type="text"
                  size="sm"
                  value={
                    patente
                  }
                  placeholder="Ej.: AE818TJ"
                  onChange={(
                    e
                  ) =>
                    setPatente(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Concesionario
                </Form.Label>

                <Form.Control
                  type="text"
                  size="sm"
                  value={
                    concesionario
                  }
                  placeholder="Buscar..."
                  onChange={(
                    e
                  ) =>
                    setConcesionario(
                      e.target
                        .value
                    )
                  }
                />
              </Col>

              <Col
                xs={12}
                md={2}
                className="mb-2"
              >
                <Form.Label>
                  Vinculación
                </Form.Label>

                <Form.Control
                  as="select"
                  size="sm"
                  value={
                    estadoVinculacion
                  }
                  onChange={(
                    e
                  ) =>
                    setEstadoVinculacion(
                      e.target
                        .value
                    )
                  }
                >
                  <option value="">
                    Todos
                  </option>

                  <option value="VINCULADO">
                    Vinculados
                  </option>

                  <option value="PATENTE_NO_ENCONTRADA">
                    Patente no encontrada
                  </option>

                  <option value="SIN_PATENTE">
                    Sin patente
                  </option>
                </Form.Control>
              </Col>

            </Row>
          </Card.Body>
        </Card>

        {error && (
          <div className="alert alert-danger py-2">
            {error}
          </div>
        )}

        {/* KPIs */}

        <Row className="mb-3">

          <Col
            xs={6}
            md={3}
            className="mb-2"
          >
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">
                  Consumos
                </small>

                <h4 className="mb-0">
                  {cantidad}
                </h4>

                <small className="text-muted">
                  Registros encontrados
                </small>
              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={6}
            md={3}
            className="mb-2"
          >
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">
                  Total consumido
                </small>

                <h4 className="mb-0 text-primary">
                  {formatoMoneda(
                    totalImporte
                  )}
                </h4>

                <small className="text-muted">
                  Según filtros aplicados
                </small>
              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={6}
            md={3}
            className="mb-2"
          >
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">
                  Vehículos
                </small>

                <h4 className="mb-0">
                  {
                    resumenVehiculos.length
                  }
                </h4>

                <small className="text-muted">
                  Patentes con consumos
                </small>
              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={6}
            md={3}
            className="mb-2"
          >
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">
                  Página
                </small>

                <h4 className="mb-0">
                  {pagina} /{" "}
                  {paginas}
                </h4>

                <small className="text-muted">
                  Hasta 100 registros por página
                </small>
              </Card.Body>
            </Card>
          </Col>

        </Row>

        {/* RESUMEN POR VEHÍCULO */}

        {resumenVehiculos.length >
          0 && (
          <Card className="mb-3">
            <Card.Body>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  Resumen por vehículo
                </h6>

                <small className="text-muted">
                  Según filtros aplicados
                </small>
              </div>

              <div className="table-responsive">
                <Table
                  striped
                  hover
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th>
                        Vehículo
                      </th>

                      <th>
                        Patente
                      </th>

                      <th className="text-right">
                        Pasadas
                      </th>

                      <th className="text-right">
                        Importe
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {resumenVehiculos.map(
                      (
                        item,
                        index
                      ) => (
                        <tr
                          key={`${item.patente}-${index}`}
                        >
                          <td className="font-weight-bold">
                            {
                              item.vehiculo
                            }
                          </td>

                          <td>
                            {
                              item.patente
                            }
                          </td>

                          <td className="text-right">
                            {
                              item.pasadas
                            }
                          </td>

                          <td className="text-right font-weight-bold">
                            {formatoMoneda(
                              item.importe
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td
                        colSpan="2"
                        className="text-right font-weight-bold"
                      >
                        TOTAL
                      </td>

                      <td className="text-right font-weight-bold">
                        {
                          todosConsumos.length
                        }
                      </td>

                      <td className="text-right font-weight-bold">
                        {formatoMoneda(
                          totalImporte
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>

            </Card.Body>
          </Card>
        )}

        {/* DETALLE */}

        <Card>
          <Card.Body>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                Detalle de consumos
              </h6>

              <small className="text-muted">
                Página {pagina} de{" "}
                {paginas}
              </small>
            </div>

            <div className="table-responsive">

              <Table
                striped
                hover
                size="sm"
              >
                <thead>
                  <tr>
                    <th>
                      Fecha
                    </th>

                    <th>
                      Hora
                    </th>

                    <th>
                      Sistema
                    </th>

                    <th>
                      Patente
                    </th>

                    <th>
                      Vehículo
                    </th>

                    <th>
                      Concesionario
                    </th>

                    <th>
                      Estación
                    </th>

                    <th>
                      Vía
                    </th>

                    <th>
                      Tag
                    </th>

                    <th className="text-right">
                      Importe
                    </th>

                    <th>
                      Estado
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {loading ? (
                    <tr>
                      <td
                        colSpan="11"
                        className="text-center py-5"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                        />

                        <span className="ml-2">
                          Cargando...
                        </span>
                      </td>
                    </tr>
                  ) : consumos.length ===
                    0 ? (
                    <tr>
                      <td
                        colSpan="11"
                        className="text-center text-muted py-4"
                      >
                        No hay consumos para los filtros seleccionados.
                      </td>
                    </tr>
                  ) : (
                    consumos.map(
                      (item) => (
                        <tr
                          key={
                            item._id
                          }
                        >
                          <td
                            style={{
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {formatoFecha(
                              item.fecha
                            )}
                          </td>

                          <td>
                            {item.hora ||
                              "-"}
                          </td>

                          <td>
                            {proveedorTexto(
                              item.proveedor
                            )}
                          </td>

                          <td className="font-weight-bold">
                            {item.patente ||
                              "-"}
                          </td>

                          <td>
                            {item.vehiculo
                              ?.camion ||
                              "-"}
                          </td>

                          <td>
                            {item.concesionario ||
                              "-"}
                          </td>

                          <td>
                            {item.estacion ||
                              "-"}
                          </td>

                          <td>
                            {item.via ||
                              "-"}
                          </td>

                          <td>
                            {item.dispositivo ||
                              "-"}
                          </td>

                          <td className="text-right font-weight-bold">
                            {formatoMoneda(
                              item.importeFinal
                            )}
                          </td>

                          <td>
                            <Badge
                              variant={estadoVariant(
                                item.estadoVinculacion
                              )}
                            >
                              {estadoTexto(
                                item.estadoVinculacion
                              )}
                            </Badge>
                          </td>
                        </tr>
                      )
                    )
                  )}

                </tbody>

                {!loading &&
                  consumos.length >
                    0 && (
                    <tfoot>
                      <tr>
                        <td
                          colSpan="9"
                          className="text-right font-weight-bold"
                        >
                          TOTAL FILTRADO
                        </td>

                        <td className="text-right font-weight-bold">
                          {formatoMoneda(
                            totalImporte
                          )}
                        </td>

                        <td />
                      </tr>
                    </tfoot>
                  )}

              </Table>
            </div>

            {/* PAGINACIÓN */}

            {paginas > 1 && (
              <div className="d-flex justify-content-center align-items-center mt-3">

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mr-2"
                  disabled={
                    pagina <=
                      1 ||
                    loading
                  }
                  onClick={() =>
                    cargarPagina(
                      pagina -
                        1
                    )
                  }
                >
                  Anterior
                </Button>

                <span className="mx-2">
                  Página{" "}
                  <strong>
                    {pagina}
                  </strong>{" "}
                  de{" "}
                  <strong>
                    {paginas}
                  </strong>
                </span>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ml-2"
                  disabled={
                    pagina >=
                      paginas ||
                    loading
                  }
                  onClick={() =>
                    cargarPagina(
                      pagina +
                        1
                    )
                  }
                >
                  Siguiente
                </Button>

              </div>
            )}

          </Card.Body>
        </Card>

      </div>
    );
  };

export default InformePeajes;